import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Users,
  MapPin,
  TrendingUp,
  MessageSquare,
  Mail,
  Search,
  Filter,
  Award,
  Calendar,
  Activity,
  User,
} from 'lucide-react';

// Champion profile data
interface ChampionProfile {
  id: string;
  name: string;
  championNumber: number;
  avatar?: string;
  neighborhood: string;
  joinedDate: string;
  bio: string;
  activityLevel: 'high' | 'medium' | 'low';
  stats: {
    neighborsReached: number;
    feedbackCollected: number;
    daysActive: number;
  };
  contact: {
    email?: string;
    phone?: string;
    preferredMethod: 'email' | 'phone' | 'platform';
  };
  sharesContact: boolean;
}

const mockChampions: ChampionProfile[] = [
  {
    id: 'champ-1',
    name: 'Michael Chen',
    championNumber: 1,
    neighborhood: 'Downtown Historic District',
    joinedDate: '2025-10-15',
    bio: 'Former city council member, passionate about community engagement. Love connecting neighbors and making Lake Charles better for everyone.',
    activityLevel: 'high',
    stats: {
      neighborsReached: 24,
      feedbackCollected: 18,
      daysActive: 15,
    },
    contact: {
      email: 'michael.chen@email.com',
      preferredMethod: 'email',
    },
    sharesContact: true,
  },
  {
    id: 'champ-2',
    name: 'Lisa Rodriguez',
    championNumber: 2,
    neighborhood: 'Lakefront Estates',
    joinedDate: '2025-10-16',
    bio: 'HOA president for 5 years. Experienced in neighborhood governance and excited to help test new community tools.',
    activityLevel: 'high',
    stats: {
      neighborsReached: 19,
      feedbackCollected: 14,
      daysActive: 14,
    },
    contact: {
      email: 'lisa.r@email.com',
      phone: '(337) 555-0102',
      preferredMethod: 'phone',
    },
    sharesContact: true,
  },
  {
    id: 'champ-3',
    name: 'Sarah Johnson',
    championNumber: 3,
    neighborhood: 'Riverside Community',
    joinedDate: '2025-10-16',
    bio: 'Teacher and mom of three. Love bringing people together and making our neighborhood feel like family.',
    activityLevel: 'medium',
    stats: {
      neighborsReached: 12,
      feedbackCollected: 8,
      daysActive: 14,
    },
    contact: {
      preferredMethod: 'platform',
    },
    sharesContact: false,
  },
  {
    id: 'champ-4',
    name: 'David Thompson',
    championNumber: 4,
    neighborhood: 'Oak Grove Subdivision',
    joinedDate: '2025-10-17',
    bio: 'Retired firefighter, active in local emergency preparedness. Interested in safety features and neighborhood watch programs.',
    activityLevel: 'medium',
    stats: {
      neighborsReached: 15,
      feedbackCollected: 11,
      daysActive: 13,
    },
    contact: {
      email: 'dthompson@email.com',
      preferredMethod: 'email',
    },
    sharesContact: true,
  },
  {
    id: 'champ-5',
    name: 'Jennifer Martinez',
    championNumber: 5,
    neighborhood: 'Sunset Hills',
    joinedDate: '2025-10-18',
    bio: 'Small business owner and community volunteer. Excited about group buying opportunities for local businesses.',
    activityLevel: 'high',
    stats: {
      neighborsReached: 22,
      feedbackCollected: 16,
      daysActive: 12,
    },
    contact: {
      email: 'jen.martinez@email.com',
      phone: '(337) 555-0105',
      preferredMethod: 'email',
    },
    sharesContact: true,
  },
  {
    id: 'champ-6',
    name: 'Robert Williams',
    championNumber: 6,
    neighborhood: 'College Park',
    joinedDate: '2025-10-19',
    bio: 'Tech professional working remotely. Interested in digital tools for community engagement.',
    activityLevel: 'medium',
    stats: {
      neighborsReached: 10,
      feedbackCollected: 7,
      daysActive: 11,
    },
    contact: {
      preferredMethod: 'platform',
    },
    sharesContact: false,
  },
  {
    id: 'champ-7',
    name: 'Angela Davis',
    championNumber: 7,
    neighborhood: 'Meadowbrook',
    joinedDate: '2025-10-20',
    bio: 'Real estate agent with deep roots in Lake Charles. Love helping neighbors and building strong communities.',
    activityLevel: 'low',
    stats: {
      neighborsReached: 6,
      feedbackCollected: 4,
      daysActive: 10,
    },
    contact: {
      email: 'angela.davis@email.com',
      preferredMethod: 'email',
    },
    sharesContact: true,
  },
  {
    id: 'champ-8',
    name: 'Marcus Brown',
    championNumber: 8,
    neighborhood: 'Pine Valley',
    joinedDate: '2025-10-21',
    bio: 'Youth sports coach and parent. Focused on family-friendly features and neighborhood events.',
    activityLevel: 'medium',
    stats: {
      neighborsReached: 13,
      feedbackCollected: 9,
      daysActive: 9,
    },
    contact: {
      phone: '(337) 555-0108',
      preferredMethod: 'phone',
    },
    sharesContact: true,
  },
  {
    id: 'champ-9',
    name: 'Emily Taylor',
    championNumber: 9,
    neighborhood: 'Heritage Square',
    joinedDate: '2025-10-22',
    bio: 'Nurse and healthcare advocate. Passionate about community health and emergency preparedness.',
    activityLevel: 'high',
    stats: {
      neighborsReached: 18,
      feedbackCollected: 13,
      daysActive: 8,
    },
    contact: {
      email: 'emily.taylor@email.com',
      preferredMethod: 'email',
    },
    sharesContact: true,
  },
  {
    id: 'champ-10',
    name: 'Christopher Lee',
    championNumber: 10,
    neighborhood: 'Westside Commons',
    joinedDate: '2025-10-23',
    bio: 'Environmental scientist interested in sustainability initiatives. Excited about community governance.',
    activityLevel: 'low',
    stats: {
      neighborsReached: 5,
      feedbackCollected: 3,
      daysActive: 7,
    },
    contact: {
      preferredMethod: 'platform',
    },
    sharesContact: false,
  },
];

const activityLevelColors = {
  high: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-gray-100 text-gray-800 border-gray-200',
};

const activityLevelLabels = {
  high: 'Very Active',
  medium: 'Active',
  low: 'Getting Started',
};

export function ChampionsDirectory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activityFilter, setActivityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [selectedChampion, setSelectedChampion] = useState<ChampionProfile | null>(null);

  // Filter champions
  const filteredChampions = mockChampions.filter((champion) => {
    // Activity filter
    if (activityFilter !== 'all' && champion.activityLevel !== activityFilter) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        champion.name.toLowerCase().includes(query) ||
        champion.neighborhood.toLowerCase().includes(query) ||
        champion.bio.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Calculate stats
  const totalChampions = mockChampions.length;
  const totalReached = mockChampions.reduce((sum, c) => sum + c.stats.neighborsReached, 0);
  const totalFeedback = mockChampions.reduce((sum, c) => sum + c.stats.feedbackCollected, 0);

  // Get initials for avatar fallback
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Champions Directory</h1>
        <p className="text-muted-foreground">
          Connect with fellow Champions across Lake Charles. Share insights and collaborate!
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{totalChampions}</p>
                <p className="text-sm text-muted-foreground">Total Champions</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{totalReached}</p>
                <p className="text-sm text-muted-foreground">Total Neighbors Reached</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{totalFeedback}</p>
                <p className="text-sm text-muted-foreground">Total Feedback Collected</p>
              </div>
              <MessageSquare className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, neighborhood, or bio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={activityFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActivityFilter('all')}
              >
                All
              </Button>
              <Button
                variant={activityFilter === 'high' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActivityFilter('high')}
              >
                Very Active
              </Button>
              <Button
                variant={activityFilter === 'medium' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActivityFilter('medium')}
              >
                Active
              </Button>
              <Button
                variant={activityFilter === 'low' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActivityFilter('low')}
              >
                Getting Started
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Champions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredChampions.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="pt-6 text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No Champions match your filters</p>
            </CardContent>
          </Card>
        ) : (
          filteredChampions.map((champion) => (
            <Card
              key={champion.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedChampion(champion)}
            >
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Avatar and Name */}
                  <div className="flex items-start gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={champion.avatar} alt={champion.name} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {getInitials(champion.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{champion.name}</h3>
                        <Badge variant="outline" className="text-xs">
                          #{champion.championNumber}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{champion.neighborhood}</span>
                      </div>
                    </div>
                  </div>

                  {/* Activity Level */}
                  <Badge className={activityLevelColors[champion.activityLevel]}>
                    <Activity className="h-3 w-3 mr-1" />
                    {activityLevelLabels[champion.activityLevel]}
                  </Badge>

                  {/* Bio */}
                  <p className="text-sm text-muted-foreground line-clamp-2">{champion.bio}</p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                    <div className="text-center">
                      <p className="text-lg font-bold">{champion.stats.neighborsReached}</p>
                      <p className="text-xs text-muted-foreground">Reached</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold">{champion.stats.feedbackCollected}</p>
                      <p className="text-xs text-muted-foreground">Feedback</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold">{champion.stats.daysActive}</p>
                      <p className="text-xs text-muted-foreground">Days</p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  {champion.sharesContact && (
                    <div className="pt-2 border-t">
                      <Button variant="outline" size="sm" className="w-full">
                        <Mail className="h-4 w-4 mr-2" />
                        Contact
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Champion Detail Modal */}
      {selectedChampion && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedChampion(null)}
        >
          <Card
            className="max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={selectedChampion.avatar} alt={selectedChampion.name} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xl">
                      {getInitials(selectedChampion.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <CardTitle>{selectedChampion.name}</CardTitle>
                      <Badge variant="outline">Champion #{selectedChampion.championNumber}</Badge>
                    </div>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <MapPin className="h-4 w-4" />
                      {selectedChampion.neighborhood}
                    </CardDescription>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedChampion(null)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Activity Level */}
              <div>
                <Badge className={activityLevelColors[selectedChampion.activityLevel]}>
                  <Activity className="h-3 w-3 mr-1" />
                  {activityLevelLabels[selectedChampion.activityLevel]}
                </Badge>
              </div>

              {/* Bio */}
              <div>
                <h4 className="font-semibold mb-2">About</h4>
                <p className="text-sm text-muted-foreground">{selectedChampion.bio}</p>
              </div>

              {/* Stats */}
              <div>
                <h4 className="font-semibold mb-3">Impact Stats</h4>
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Users className="h-6 w-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">
                        {selectedChampion.stats.neighborsReached}
                      </p>
                      <p className="text-xs text-muted-foreground">Neighbors Reached</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <MessageSquare className="h-6 w-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">
                        {selectedChampion.stats.feedbackCollected}
                      </p>
                      <p className="text-xs text-muted-foreground">Feedback Collected</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Calendar className="h-6 w-6 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold">{selectedChampion.stats.daysActive}</p>
                      <p className="text-xs text-muted-foreground">Days Active</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="font-semibold mb-3">Contact Information</h4>
                {selectedChampion.sharesContact ? (
                  <div className="space-y-2">
                    {selectedChampion.contact.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedChampion.contact.email}</span>
                      </div>
                    )}
                    {selectedChampion.contact.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedChampion.contact.phone}</span>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Preferred contact: {selectedChampion.contact.preferredMethod}
                    </p>
                    <Button className="w-full mt-4">
                      <Mail className="h-4 w-4 mr-2" />
                      Send Message
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    This Champion prefers to communicate through the platform messaging system.
                  </p>
                )}
              </div>

              {/* Joined Date */}
              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  Joined{' '}
                  {new Date(selectedChampion.joinedDate).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
